import { createContext, useContext, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { TopNav } from './TopNav'
import { Sidebar } from './Sidebar'
import { Breadcrumb } from './Breadcrumb'

type LayoutType = 'default' | 'runner';

const LayoutContext = createContext<{ setLayout: (layout: LayoutType) => void }>({ setLayout: () => {} });

/** Standard app shell: collapsible `<Sidebar>` + `<TopNav>` + `<Outlet>` content area. */
export function DefaultLayout() {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50">
      <TopNav onToggleSidebar={() => setCollapsed((c) => !c)} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar collapsed={collapsed} />
        <main className="flex-1 overflow-y-auto p-6">
          <Breadcrumb />
          <Outlet />
        </main>
      </div>
    </div>
  )

}

/** Minimal layout for the test runner — full-width `<Outlet>` without sidebar or top nav. */
export function RunnerLayout() {
  const [collapsed, setCollapsed] = useState(true)
  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-50">
      <TopNav onToggleSidebar={() => setCollapsed((c) => !c)} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar collapsed={collapsed} />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}


/** Root layout component that delegates to `DefaultLayout` or `RunnerLayout` based on `useLayoutSelection`. */
export function AppLayout() {
  const [layout, setLayout] = useState<LayoutType>("default");
  let Layout;
  switch(layout) {
    case "default":
      Layout = DefaultLayout;
      break;
    case "runner":
      Layout = RunnerLayout;
      break;
    default:
      Layout = DefaultLayout;
  }
  return <LayoutContext.Provider value={{ setLayout }}><Layout /></LayoutContext.Provider>;
}

/**
 * Hook that reads the current route match to choose between `'default'`
 * and `'runner'` layout. Returns `'runner'` when the route handle has
 * `layout: 'runner'`; otherwise returns `'default'`.
 */
export function useLayoutSelection() {
  return useContext(LayoutContext);
}